"use client";

import "@assistant-ui/react-markdown/styles/dot.css";

import {
  type CodeHeaderProps,
  MarkdownTextPrimitive,
  unstable_memoizeMarkdownComponents as memoizeMarkdownComponents,
  useIsMarkdownCodeBlock,
} from "@assistant-ui/react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import { defaultUrlTransform } from "react-markdown";
import { type FC, memo, useCallback, useMemo, useRef, useState } from "react";
import { CheckIcon, CopyIcon, InfoIcon } from "lucide-react";
import { useAuiState } from "@assistant-ui/react";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import type { DocumentCitationData, WebSourceData } from "@/components/custom/chatModelAdapters";
import { findUnstableEntry } from "@/lib/assistantUiTypes";

import { TooltipIconButton } from "@/components/assistant-ui/tooltip-icon-button";
import { cn } from "@/lib/utils";
import { navigateToCitation } from "@/components/assistant";
import { TableExportHeader } from "@/components/assistant-ui/table-export-header";
import { MermaidDiagram, MermaidCodeHeader } from "@/components/assistant-ui/mermaid-diagram";
import { useTranslation } from "react-i18next";
import { useLinkPreview } from "@/hooks/use-link-preview";
import { useCopyToClipboard } from "@/hooks/use-copy-to-clipboard";

const EMPTY_CITATIONS: readonly DocumentCitationData[] = [];
const EMPTY_WEB_SOURCES: readonly WebSourceData[] = [];

function citeUrlTransform(url: string): string {
  if (url.startsWith("cite:")) return url;
  return defaultUrlTransform(url);
}

const LANGUAGE_COMPONENTS = {
  mermaid: {
    SyntaxHighlighter: MermaidDiagram,
    CodeHeader: MermaidCodeHeader,
  },
};

function preprocessCitations(text: string): string {
  // Convert [label](cite:...) and [label](https://www.aspi...) markdown links
  // to <a> tags so they render inside HTML blocks (tables) where remark won't
  // parse markdown syntax.
  return text.replace(
    /\[([^\]]+)\]\(((?:cite:|https:\/\/www\.aspi\.)[^)]+)\)/g,
    '<a href="$2">$1</a>',
  );
}

function preprocessMarkdown(text: string): string {
  return preprocessCitations(text);
}

// HTML-escape minimal special chars for our injected attributes / sup label.
// We never trust API-supplied strings as raw HTML; rehype-raw downstream parses
// the result, so unescaped quotes / angle brackets in titles or snippets would
// otherwise break attribute parsing or inject elements.
function escapeHtmlAttr(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

// Escape regex metacharacters so a user's cited_text (which may contain `(`, `?`,
// `.`, etc.) can be searched as a literal substring.
function escapeRegex(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Walks the web-source list (in delivery order) and injects a `<websrc>` marker
 * immediately after the first occurrence of each source's `citedText`. Multiple
 * sources targeting the exact same span are inserted at the same insertion
 * point so the renderer can group them as `[1][2]`. The deduped-URL position
 * (1-based) is what's displayed.
 *
 * Robustness: substring match is whitespace-sensitive. Anthropic's `cited_text`
 * usually substring-matches the prose verbatim, but markdown-source mutation
 * (e.g. `**bold**` insertions) or paragraph wrapping can break the match. A
 * miss → no inline marker, but the source still appears in the Sources footer
 * via metadata.unstable_data.
 */
function injectWebSourceMarkers(text: string, sources: WebSourceData[]): string {
  if (sources.length === 0) return text;

  // Build deduped URL → 1-based number table (footer-and-inline are consistent).
  const urlToNumber = new Map<string, number>();
  for (const s of sources) {
    if (!urlToNumber.has(s.url)) urlToNumber.set(s.url, urlToNumber.size + 1);
  }

  let result = text;
  for (const s of sources) {
    if (!s.citedText) continue;
    const number = urlToNumber.get(s.url);
    if (number === undefined) continue;

    const idx = result.indexOf(s.citedText);
    if (idx === -1) continue;

    const insertAt = idx + s.citedText.length;
    const marker = `<websrc data-n="${number}" data-url="${escapeHtmlAttr(s.url)}" data-snippet="${escapeHtmlAttr(s.citedText ?? "")}" data-title="${escapeHtmlAttr(s.title ?? "")}"></websrc>`;
    result = result.slice(0, insertAt) + marker + result.slice(insertAt);
  }
  return result;
}

function useWebSourcesFromMetadata(): WebSourceData[] {
  return useAuiState(({ message }) =>
    findUnstableEntry(message.metadata?.unstable_data, "web-sources")?.sources ?? EMPTY_WEB_SOURCES
  ) as WebSourceData[];
}

const MarkdownTextImpl = () => {
  const webSources = useWebSourcesFromMetadata();

  // Stable preprocess closure that combines the static document-block rewrite
  // with metadata-driven web-source marker injection. Re-allocated only when
  // the web-source list changes (≤ a handful of times per message).
  const preprocess = useMemo(() => {
    return (text: string) => injectWebSourceMarkers(preprocessMarkdown(text), webSources);
  }, [webSources]);

  return (
      <MarkdownTextPrimitive
        smooth={true}
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw]}
        preprocess={preprocess}
        className="aui-md mt-3 font-body"
        components={memoizedComponents}
        componentsByLanguage={LANGUAGE_COMPONENTS}
        urlTransform={citeUrlTransform}
      />
  );
};

export const MarkdownText = memo(MarkdownTextImpl);

const CodeHeader: FC<CodeHeaderProps> = ({ language, code }) => {
  const { t } = useTranslation();
  const { isCopied, copyToClipboard } = useCopyToClipboard();
  const onCopy = () => {
    if (!code || isCopied) return;
    copyToClipboard(code);
  };

  return (
    <div className="aui-code-header-root mt-4 flex items-center justify-between gap-4 rounded-t-lg bg-muted-foreground/15 px-4 py-2 font-mono font-semibold text-foreground text-sm dark:bg-muted-foreground/20">
      <span className="aui-code-header-language lowercase [&>span]:text-xs">
        {language}
      </span>
      <TooltipIconButton tooltip={t("markdown.copy")} onClick={onCopy}>
        {!isCopied && <CopyIcon />}
        {isCopied && <CheckIcon />}
      </TooltipIconButton>
    </div>
  );
};

function parseCiteHref(href: string): { docId: string; pages?: string; citationIndex?: number } {
  const raw = href.slice("cite:".length); // "docId?pages=5&ci=0"
  const qIdx = raw.indexOf("?");
  if (qIdx === -1) return { docId: raw };
  const docId = raw.slice(0, qIdx);
  const params = new URLSearchParams(raw.slice(qIdx + 1));
  const ci = params.get("ci");
  return {
    docId,
    pages: params.get("pages") ?? undefined,
    citationIndex: ci != null ? parseInt(ci, 10) : undefined,
  };
}


function useHoverPopover(delay = 150) {
  const [open, setOpen] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const enter = useCallback(() => { clearTimeout(timeoutRef.current); setOpen(true); }, []);
  const leave = useCallback(() => { clearTimeout(timeoutRef.current); timeoutRef.current = setTimeout(() => setOpen(false), delay); }, [delay]);
  return { open, setOpen, triggerProps: { onMouseEnter: enter, onMouseLeave: leave }, contentProps: { onMouseEnter: enter, onMouseLeave: leave } };
}

function AspiLink({ href, children }: { href: string; className?: string; children: React.ReactNode }) {
  const { t } = useTranslation();
  const hover = useHoverPopover();
  const preview = useLinkPreview(href);
  const childText = typeof children === "string" ? children
    : Array.isArray(children) ? children.filter((c) => typeof c === "string").join("")
    : "";
  // If the link text is just the URL itself (autolinked), prefer the fetched page title
  const isUrl = childText.startsWith("http://") || childText.startsWith("https://");
  const inlineTitle = isUrl ? (preview.title || "ASPI") : (childText || preview.title || "ASPI");

  return (
    <PopoverPrimitive.Root open={hover.open} onOpenChange={hover.setOpen}>
      <PopoverPrimitive.Anchor asChild>
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="aui-md-citation inline-flex items-center gap-1 rounded-md px-1 py-0.5 hover:bg-muted/60 transition-colors cursor-pointer align-middle"
          {...hover.triggerProps}
        >
          <InfoIcon className="size-3 text-muted-foreground shrink-0" />
          <span className="text-[10px] text-muted-foreground">
            {inlineTitle.length > 20 ? inlineTitle.slice(0, 20) + "…" : inlineTitle}
          </span>
        </a>
      </PopoverPrimitive.Anchor>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          side="right"
          align="start"
          sideOffset={4}
          onOpenAutoFocus={(e) => e.preventDefault()}
          onCloseAutoFocus={(e) => e.preventDefault()}
          className="z-50 w-64 overflow-hidden rounded-xl bg-popover shadow-md outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
          {...hover.contentProps}
        >
          <div className="flex items-start transition-colors hover:bg-muted/60">
            <button
              type="button"
              className="flex flex-1 items-start gap-2 px-3 py-2.5 text-left cursor-pointer min-w-0"
              onClick={() => { hover.setOpen(false); window.open(href, "_blank"); }}
            >
              <img src="/aspi-avatar.png" alt="ASPI" className="size-3.5 rounded-full shrink-0 mt-0.5" />
              <div className="flex flex-col gap-0.5 min-w-0">
                <span className="text-xs font-medium text-foreground line-clamp-1">
                  {preview.title || inlineTitle}
                </span>
                {preview.description ? (
                  <span className="text-xs text-foreground/70 line-clamp-2 leading-snug">
                    {preview.description}
                  </span>
                ) : preview.loaded ? (
                  <span className="text-[10px] text-muted-foreground italic">
                    {t("links.missingPreview")}
                  </span>
                ) : null}
              </div>
            </button>
            <CopyUrlButton url={href} />
          </div>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}

function CitationLink({ href, children }: { href: string; className?: string; children: React.ReactNode }) {
  const hover = useHoverPopover();
  const { docId, pages, citationIndex } = useMemo(() => parseCiteHref(href), [href]);

  const allCitations = useAuiState(({ message }) =>
    findUnstableEntry(message.metadata?.unstable_data, "citations")?.citations ?? EMPTY_CITATIONS
  ) as readonly DocumentCitationData[];

  const citation = useMemo(() => {
    if (citationIndex != null && citationIndex >= 0 && citationIndex < allCitations.length) {
      return allCitations[citationIndex];
    }
    return (
      allCitations.find((c) => c.docId === docId && c.pages === pages) ??
      allCitations.find((c) => c.docId === docId)
    );
  }, [allCitations, docId, pages, citationIndex]);

  const childText = typeof children === "string" ? children
    : Array.isArray(children) ? children.filter((c) => typeof c === "string").join("")
    : "";
  const label = citation?.name ?? (childText || docId);
  const truncated = label.length > 20 ? label.slice(0, 20) + "…" : label;

  return (
    <PopoverPrimitive.Root open={hover.open} onOpenChange={hover.setOpen}>
      <PopoverPrimitive.Anchor asChild>
        <a
          href={href}
          className="aui-md-citation inline-flex items-center gap-1 rounded-md px-1 py-0.5 hover:bg-muted/60 transition-colors cursor-pointer align-middle"
          {...hover.triggerProps}
          onClick={(e) => {
            e.preventDefault();
            navigateToCitation(citation ?? { docId, pages }, allCitations);
          }}
        >
          <InfoIcon className="size-3 text-muted-foreground shrink-0" />
          <span className="text-[10px] text-muted-foreground">{truncated}</span>
        </a>
      </PopoverPrimitive.Anchor>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          side="right"
          align="start"
          sideOffset={4}
          onOpenAutoFocus={(e) => e.preventDefault()}
          onCloseAutoFocus={(e) => e.preventDefault()}
          className="z-50 w-64 overflow-hidden rounded-xl bg-popover shadow-md outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
          {...hover.contentProps}
        >
          <div className="flex items-start transition-colors hover:bg-muted/60">
            <button
              type="button"
              className="flex flex-1 items-start gap-2 px-3 py-2.5 text-left cursor-pointer min-w-0"
              onClick={() => { hover.setOpen(false); navigateToCitation(citation ?? { docId, pages }, allCitations); }}
            >
              <img src="/praetor-avatar.png" alt="Praetor" className="size-3.5 rounded-full shrink-0 mt-0.5" />
              <div className="flex flex-col gap-0.5 min-w-0">
                <span className="text-xs font-medium text-foreground line-clamp-1">
                  {citation?.name ?? (childText || docId)}
                </span>
                {citation?.snippet && (
                  <span className="text-xs text-foreground/80 line-clamp-2 leading-snug">
                    &ldquo;{citation.snippet}&rdquo;
                  </span>
                )}
              </div>
            </button>
            <CopyUrlButton url={citation?.name ?? (childText || docId)} />
          </div>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}

function ExternalLinkIcon({ href, children }: { href: string; children: React.ReactNode }) {
  const { t } = useTranslation();
  const hover = useHoverPopover();
  const label = typeof children === "string" ? children : "";
  const domain = useMemo(() => {
    try { return new URL(href).hostname; } catch { return undefined; }
  }, [href]);
  const faviconUrl = domain
    ? `https://www.google.com/s2/favicons?domain=${domain}&sz=32`
    : "/praetor-avatar.png";
  const preview = useLinkPreview(href);
  const title = preview.title || label || domain;

  return (
    <PopoverPrimitive.Root open={hover.open} onOpenChange={hover.setOpen}>
      <PopoverPrimitive.Anchor asChild>
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="aui-md-citation inline-flex items-center gap-1 rounded-md px-1 py-0.5 hover:bg-muted/60 transition-colors cursor-pointer align-middle"
          {...hover.triggerProps}
        >
          <InfoIcon className="size-3 text-muted-foreground shrink-0" />
          {title && (
            <span className="text-[10px] text-muted-foreground line-clamp-1 max-w-[140px]">
              {title}
            </span>
          )}
        </a>
      </PopoverPrimitive.Anchor>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          side="right"
          align="start"
          sideOffset={4}
          onOpenAutoFocus={(e) => e.preventDefault()}
          onCloseAutoFocus={(e) => e.preventDefault()}
          className="z-50 w-64 overflow-hidden rounded-xl bg-popover shadow-md outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
          {...hover.contentProps}
        >
          <div className="flex items-start transition-colors hover:bg-muted/60">
            <button
              type="button"
              className="flex flex-1 items-start gap-2 px-3 py-2.5 text-left cursor-pointer min-w-0"
              onClick={() => { hover.setOpen(false); window.open(href, "_blank"); }}
            >
              <img src={faviconUrl} alt={domain ?? t("markdown.linkLabel")} className="size-3.5 rounded-full shrink-0 mt-0.5" />
              <div className="flex flex-col gap-0.5 min-w-0">
                {title && (
                  <span className="text-xs font-medium text-foreground line-clamp-1">
                    {title}
                  </span>
                )}
                {preview.description ? (
                  <span className="text-xs text-foreground/70 line-clamp-2 leading-snug">
                    {preview.description}
                  </span>
                ) : preview.loaded ? (
                  <span className="text-[10px] text-muted-foreground italic">
                    {t("links.missingPreview")}
                  </span>
                ) : null}
              </div>
            </button>
            <CopyUrlButton url={href} />
          </div>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}

function CopyUrlButton({ url }: { url: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      navigator.clipboard.writeText(url).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
    },
    [url],
  );
  return (
    <button
      type="button"
      onClick={handleCopy}
      className="shrink-0 p-2 text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
    >
      {copied ? <CheckIcon className="size-3" /> : <CopyIcon className="size-3" />}
    </button>
  );
}

const memoizedComponents = memoizeMarkdownComponents({
  h1: ({ className, ...props }) => (
    <h1
      className={cn(
        "aui-md-h1 mb-5 scroll-m-20 font-semibold text-2xl tracking-tight last:mb-0",
        className,
      )}
      {...props}
    />
  ),
  h2: ({ className, ...props }) => (
    <h2
      className={cn(
        "aui-md-h2 mt-6 mb-3 scroll-m-20 font-semibold text-xl tracking-tight first:mt-0 last:mb-0",
        className,
      )}
      {...props}
    />
  ),
  h3: ({ className, ...props }) => (
    <h3
      className={cn(
        "aui-md-h3 mt-5 mb-3 scroll-m-20 font-semibold text-lg tracking-tight first:mt-0 last:mb-0",
        className,
      )}
      {...props}
    />
  ),
  h4: ({ className, ...props }) => (
    <h4
      className={cn(
        "aui-md-h4 mt-5 mb-3 scroll-m-20 font-semibold text-lg tracking-tight first:mt-0 last:mb-0",
        className,
      )}
      {...props}
    />
  ),
  h5: ({ className, ...props }) => (
    <h5
      className={cn(
        "aui-md-h5 my-3 font-semibold text-lg tracking-tight first:mt-0 last:mb-0",
        className,
      )}
      {...props}
    />
  ),
  h6: ({ className, ...props }) => (
    <h6
      className={cn(
        "aui-md-h6 my-3 font-semibold text-lg tracking-tight first:mt-0 last:mb-0",
        className,
      )}
      {...props}
    />
  ),
  p: ({ className, ...props }) => (
    <p
      className={cn(
        "aui-md-p mt-5 mb-5 leading-7 first:mt-0 last:mb-0",
        className,
      )}
      {...props}
    />
  ),
  a: function MarkdownLink({ className, href, children, ...props }) {
    if (href?.startsWith("cite:")) {
      return <CitationLink href={href} className={className}>{children}</CitationLink>;
    }

    if (href?.startsWith("https://www.aspi.")) {
      return <AspiLink href={href} className={className}>{children}</AspiLink>;
    }

    if (href?.startsWith("http://") || href?.startsWith("https://")) {
      return <ExternalLinkIcon href={href}>{children}</ExternalLinkIcon>;
    }

    return (
      <a
        className={cn(
          "aui-md-a font-medium text-primary underline underline-offset-4",
          className,
        )}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        {...props}
      >
        {children}
      </a>
    );
  },
  blockquote: ({ className, ...props }) => (
    <blockquote
      className={cn("aui-md-blockquote border-l-2 pl-6 italic", className)}
      {...props}
    />
  ),
  ul: ({ className, ...props }) => (
    <ul
      className={cn("aui-md-ul my-5 ml-6 list-disc [&>li]:mt-2", className)}
      {...props}
    />
  ),
  ol: ({ className, ...props }) => (
    <ol
      className={cn("aui-md-ol my-5 ml-6 list-decimal [&>li]:mt-2", className)}
      {...props}
    />
  ),
  hr: ({ className, ...props }) => (
    <hr className={cn("aui-md-hr my-5 border-b", className)} {...props} />
  ),
  table: function TableWrapper({ className, ...props }) {
    const tableRef = useRef<HTMLTableElement>(null);
    return (
      <div className="group relative my-5 overflow-x-auto">
        <TableExportHeader tableRef={tableRef} />
        <table
          ref={tableRef}
          className={cn(
            "aui-md-table w-full border-separate border-spacing-0 overflow-y-auto",
            className,
          )}
          {...props}
        />
      </div>
    );
  },
  th: ({ className, ...props }) => (
    <th
      className={cn(
        "aui-md-th bg-muted px-4 py-2 text-left font-bold first:rounded-tl-lg last:rounded-tr-lg [[align=center]]:text-center [[align=right]]:text-right",
        className,
      )}
      {...props}
    />
  ),
  td: ({ className, ...props }) => (
    <td
      className={cn(
        "aui-md-td border-b border-l px-4 py-2 text-left last:border-r [[align=center]]:text-center [[align=right]]:text-right",
        className,
      )}
      {...props}
    />
  ),
  tr: ({ className, ...props }) => (
    <tr
      className={cn(
        "aui-md-tr m-0 border-b p-0 first:border-t [&:last-child>td:first-child]:rounded-bl-lg [&:last-child>td:last-child]:rounded-br-lg",
        className,
      )}
      {...props}
    />
  ),
  sup: ({ className, ...props }) => (
    <sup
      className={cn("aui-md-sup [&>a]:text-xs [&>a]:no-underline", className)}
      {...props}
    />
  ),
  pre: ({ className, ...props }) => (
    <pre
      className={cn(
        "aui-md-pre overflow-x-auto rounded-t-none! rounded-b-lg bg-black p-4 font-mono text-white",
        className,
      )}
      {...props}
    />
  ),
  code: function Code({ className, ...props }) {
    const isCodeBlock = useIsMarkdownCodeBlock();
    return (
      <code
        className={cn(
          "font-mono",
          !isCodeBlock &&
            "aui-md-inline-code rounded border bg-muted font-semibold",
          className,
        )}
        {...props}
      />
    );
  },
  // Inline web-search citation marker. Rendered as a hoverable superscript
  // that links to the source URL. Data attributes come from the preprocess
  // pass in `injectWebSourceMarkers` and are escaped at injection time.
  ["websrc" as any]: WebSourceMarker,
  CodeHeader
});

function WebSourceMarker({
  ["data-n"]: numberAttr,
  ["data-url"]: url,
  ["data-snippet"]: snippet,
  ["data-title"]: title,
}: {
  "data-n"?: string;
  "data-url"?: string;
  "data-snippet"?: string;
  "data-title"?: string;
}) {
  const hover = useHoverPopover();
  const number = numberAttr ? Number.parseInt(numberAttr, 10) : NaN;
  if (!url || !Number.isFinite(number)) return null;

  let hostname = url;
  try { hostname = new URL(url).hostname; } catch { /* fall back to raw url */ }

  return (
    <PopoverPrimitive.Root open={hover.open} onOpenChange={hover.setOpen}>
      <PopoverPrimitive.Anchor asChild>
        <sup
          className="aui-md-websrc inline align-super text-[0.65em] leading-none ml-0.5 select-none"
          {...hover.triggerProps}
        >
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="rounded px-1 py-0.5 font-mono text-muted-foreground hover:bg-muted/60 hover:text-foreground transition-colors no-underline"
            onClick={(e) => { e.stopPropagation(); }}
          >
            [{number}]
          </a>
        </sup>
      </PopoverPrimitive.Anchor>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          side="top"
          align="start"
          sideOffset={4}
          onOpenAutoFocus={(e) => e.preventDefault()}
          onCloseAutoFocus={(e) => e.preventDefault()}
          className="z-50 w-72 overflow-hidden rounded-xl bg-popover shadow-md outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
          {...hover.contentProps}
        >
          <div className="flex flex-col gap-1 px-3 py-2">
            <span className="text-xs font-medium text-foreground line-clamp-2">
              {title || hostname}
            </span>
            <span className="text-[10px] text-muted-foreground truncate">{hostname}</span>
            {snippet ? (
              <span className="text-xs text-foreground/80 line-clamp-3 leading-snug mt-1">
                &ldquo;{snippet}&rdquo;
              </span>
            ) : null}
          </div>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}
